import TermsAndConditions from '@base/features/termsAndConditions/termsAndConditions';
import { ITableSettingsService } from '@base/features/table/interfaces';
import GinRummyMinPoints from './ginRummyMinPoints';
import { t, useBehaviorSubject } from '@base/utils/helpers';
import { container } from 'tsyringe';
import styles from '../styles/createTable.module.scss';
import { ISocketClient } from '@base/features/socket/interfaces';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IUserService } from '@base/features/user/model/interfaces';
import { ITicketService } from '@base/features/tickets/model/interfaces';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import CreateTableService from '../model/createTableService';
import MainButton from '@ui-kit/components/mainButton';
import CreateTableBlock from './createTableBlock';
import BackgammonOption from './backgammonOption';
import CribbageOption from './cribbageOption';
import { CreateTablePickColor } from './createTablePickColor';
import FlexRow from '@base/ui/components/flexRow';
import { ConnectionType } from '@base/features/game/model/interfaces';
import { CreateTableTimePerMove } from './createTableTimePerMove';
import { CreateTableMatchPoints } from './createTableMatchPoints';
import { CreateTableEntryFee } from './createTableEntryFee';
import { Checkbox } from '@ui-kit/components/checkbox';
import lightning from '@ui-kit/icons/lightning.svg';
import Codes from '@base/features/socket/codes';
import { MAIN_ROUTES } from '@base/features/routing/constants/mainRoutes';
import GinRummyOption from './ginRummyOption';
import { gameTypes } from '@gin-rummy/constants/codes';
import { objectKeys } from '@utils/functions';

export const CreateTable = () => {
  const socket: ISocketClient = container.resolve('socketClient');
  const [isTourney, setIsTourney] = useState(false);
  // const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');

  useEffect(() => {
    setIsTourney(!socket.isConnectedToDefault());
  }, []);

  const history = useHistory();
  const userService: IUserService = container.resolve('userService');
  const [loading, setLoading] = useState(false);
  const ticketsService: ITicketService = container.resolve('ticketsService');
  const tableSettingsRepository: ITableSettingsService = container.resolve('tableSettingsRepository');
  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const createTableService: CreateTableService = container.resolve('createTableService');
  const options = useBehaviorSubject(tableSettingsRepository.settings);
  const isTicketPlayer = playersOnlineRepository.me?.isTicketPlayer();

  return (
    <>
      <h1 className={styles.title}>{t('create_table')}</h1>
      <div className={styles.settings}>
        <div className={styles.column}>
          <CreateTableBlock tooltip="tooltip-buy_tickets">
            <>
              <div className={styles.ticket}>
                <div className={styles.bodyTitle}>{t('ticket_game')}</div>
                <TermsAndConditions
                  id="base_game_terms"
                  status={createTableService.isTicketGame}
                  handleStatus={createTableService.handlerTicketGame.bind(createTableService)}
                  classWrap={styles.bodyWrap}
                />
              </div>

              <div className={styles.entryWrap}>
                {createTableService.isTicketGame && (
                  <CreateTableEntryFee
                    cost={options.cost}
                    typeGame={ConnectionType.CONNECT_DEFAULT}
                    handlerEntryFee={createTableService.handlerEntryFee.bind(createTableService)}
                  />
                )}
              </div>

              <div className={styles.buyTickets}>
                <MainButton
                  className="create-table__btn-buy"
                  onClick={async () => {
                    if (userService.isGuestPlayer())
                      return userService.openGuestModal(() => history.push(MAIN_ROUTES.register));
                    setLoading(true);
                    await ticketsService.openPaymentModal();
                    setLoading(false);
                  }}
                  loading={loading}
                >
                  {t('buy_tickets')}
                </MainButton>
              </div>
            </>
          </CreateTableBlock>

          <CreateTableBlock tooltip="tooltip-group_option">
            <div className={styles.groupOptions}>
              <Checkbox
                id="rated-game"
                checked={options.ratingGame}
                name="create-table"
                handler={createTableService.handlerRatedGame.bind(createTableService)}
                disabled={options.ladder || createTableService.isTicketGame}
              >
                {t('rated_game')}
              </Checkbox>

              <Checkbox
                id="ladder"
                checked={options.ladder}
                name="create-table"
                handler={createTableService.handlerLadderGame.bind(createTableService)}
              >
                {t('ladder')}
              </Checkbox>

              <Checkbox
                id="invitation"
                checked={options.inviteOnly}
                name="create-table"
                handler={createTableService.handlerInvitation.bind(createTableService)}
              >
                {t('invitation_only')}
              </Checkbox>

              <BackgammonOption>
                <Checkbox
                  id="double"
                  checked={options.minPoints === 0}
                  name="create-table"
                  disabled={options.maxPoints === 1}
                  handler={(e) => createTableService.handlerDoubleOption(e.target.checked)}
                >
                  {t('no_double')}
                </Checkbox>
              </BackgammonOption>

              <BackgammonOption>
                <Checkbox
                  id="crawford"
                  checked={options.minPoints === -1}
                  name="create-table"
                  disabled={options.maxPoints === 1}
                  handler={createTableService.handlerCrawford.bind(createTableService)}
                >
                  {t('crawford')}
                </Checkbox>
              </BackgammonOption>

              <GinRummyOption>
                <Checkbox
                  id="looser-deals"
                  checked={createTableService.getSign(Codes.TableFlags.LOSDEAL)}
                  name="create-table"
                  handler={(e) => createTableService.handlerLooserDeals(e.target.checked)}
                  disabled={false}
                >
                  {t('looser_deals')}
                </Checkbox>
              </GinRummyOption>

              <CribbageOption>
                <Checkbox
                  id="twogamematch"
                  checked={createTableService.getSign(Codes.TableFlags.TWOGAMEMATCH)}
                  name="create-table"
                  handler={(e) => createTableService.handlerTwoGameMatch(e.target.checked)}
                >
                  {t('2/3_game')}
                </Checkbox>
              </CribbageOption>

              <CribbageOption>
                <Checkbox
                  id="discard"
                  checked={createTableService.getSign(Codes.TableFlags.DISCARD)}
                  name="create-table"
                  handler={(e) => createTableService.handlerDiscard(e.target.checked)}
                >
                  {t('show_played_cards')}
                </Checkbox>
              </CribbageOption>

              <CribbageOption>
                <Checkbox
                  id="manual-scoring"
                  checked={createTableService.getSign(Codes.TableFlags.MANUAL)}
                  name="create-table"
                  disabled={true}
                  handler={(e) => createTableService.handlerManual(e.target.checked)}
                >
                  {t('manual_scoring')}
                </Checkbox>
              </CribbageOption>

              <Checkbox
                id="watchers"
                name="create-table"
                checked={options.noWatchers}
                handler={createTableService.handlerWatchers.bind(createTableService)}
                disabled={!isTicketPlayer}
              >
                <div>
                  <span>{t('no_watchers')}</span>
                  {!isTicketPlayer ? (
                    <div className={styles.footnote}>{t('for_ticket_players_only')}</div>
                  ) : null}
                </div>
              </Checkbox>
            </div>
          </CreateTableBlock>
        </div>

        <div className={styles.column}>
          <BackgammonOption>
            <CreateTableBlock tooltip="tooltip-match_points">
              <CreateTableMatchPoints
                value={options.maxPoints}
                handlerMatchPointsFee={createTableService.handlerMatchPointsFee.bind(createTableService)}
                disabled={isTourney}
              />
            </CreateTableBlock>
          </BackgammonOption>

          <CreateTableBlock tooltip="tooltip-time_per_move">
            <CreateTableTimePerMove
              value={options.timePerMove}
              handlerTimePerMove={createTableService.handlerTimePerMove.bind(createTableService)}
              disabled={isTourney}
            />
          </CreateTableBlock>

          <CribbageOption>
            <CreateTableBlock tooltip="cribbage_min-points">
              <FlexRow justify="space-between">
                {[0, 1].map((minPoints) => (
                  <FlexRow>
                    <input
                      type="radio"
                      id={`points_${minPoints}`}
                      name="minPoints"
                      className={isTourney ? 'disabled' : undefined}
                      checked={minPoints === options.minPoints}
                      onChange={() => createTableService.handlerMinPoints(minPoints)}
                    />
                    <label htmlFor={`points_${minPoints}`}>
                      <span>{minPoints === 0 ? t('six_cards') : t('five_cards')}</span>
                    </label>
                  </FlexRow>
                ))}
              </FlexRow>
            </CreateTableBlock>
          </CribbageOption>

          <CribbageOption>
            <CreateTableBlock tooltip="cribbage_max-points">
              <FlexRow justify="space-between">
                {[1, 2].map((maxPoints) => (
                  <FlexRow>
                    <input
                      type="radio"
                      id={`points_${maxPoints}`}
                      name="maxPoints"
                      className={isTourney ? 'disabled' : undefined}
                      checked={maxPoints === options.maxPoints}
                      onChange={() => createTableService.handlerMaxPoints(maxPoints)}
                    />
                    <label htmlFor={`points_${maxPoints}`}>
                      <span>{maxPoints === 1 ? `61 ${t('points')}` : `121 ${t('points')}`}</span>
                    </label>
                  </FlexRow>
                ))}
              </FlexRow>
            </CreateTableBlock>
          </CribbageOption>

          <GinRummyOption>
            <CreateTableBlock tooltip="ginrummy_min-points">
              <FlexRow justify="space-between">
                {objectKeys(gameTypes).map((gameType) => (
                  <FlexRow>
                    <input
                      type="radio"
                      id={gameType}
                      name="minPoints"
                      className={isTourney ? 'disabled' : undefined}
                      checked={gameTypes[gameType] === options.minPoints}
                      onChange={() => createTableService.handlerMinPoints(gameTypes[gameType])}
                    />
                    <label htmlFor={gameType}>
                      <span>{t(gameType)}</span>
                    </label>
                  </FlexRow>
                ))}
              </FlexRow>
            </CreateTableBlock>
          </GinRummyOption>

          <GinRummyOption>
            <CreateTableBlock tooltip="ginrummy_max_points">
              <GinRummyMinPoints
                value={options.maxPoints}
                handlerMatchPointsFee={createTableService.handlerMaxPoints.bind(createTableService)}
                disabled={isTourney}
              />
            </CreateTableBlock>
          </GinRummyOption>

          <BackgammonOption>
            <CreateTableBlock>
              <CreateTablePickColor
                gameColor={options.gameColor}
                handlerPickColor={createTableService.handlerPickColor.bind(createTableService)}
              />
            </CreateTableBlock>
          </BackgammonOption>

        </div>
      </div>

      <MainButton className={styles.mainBtn} onClick={() => createTableService.createTable()}>
        <img className={styles.mainBtnImg} src={lightning} alt="lightning" />
        {t('create')}
      </MainButton>
    </>
  );
};
